import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    Button,
    Alert,
    TextField,
    FormControlLabel,
    Switch,
    Snackbar, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import Protected from "../../components/Protected";
import {ProtectedFieldSet} from "../../context/AuthDisabledContext";
import {ROLES} from "../../config/roles";

const PrizeCategoryEditPage = () => {
    const {id} = useParams(); // 'id' will be 'new' or a number
    const navigate = useNavigate();
    const isNew = id === 'new';

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [sortOrder, setSortOrder] = useState(0);
    const [active, setActive] = useState(true);

    // Page state
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    // Fetch existing category data if in "edit" mode
    useEffect(() => {
        if (!isNew) {
            setLoading(true);
            // NOTE: This assumes you have a GET /categories/{id} endpoint.
            // If not, you'll need to filter the result from the main categories list.
            apiService.get(`/api/admin/prizes/categories/${id}`)
                .then(data => {
                    setName(data.name);
                    setDescription(data.description);
                    setSortOrder(data.sortOrder);
                    setActive(data.active);
                })
                .catch(err => setError(err.message || 'Failed to load category'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError(null);

        const categoryData = {
            id: isNew ? null : id,
            name,
            description,
            sortOrder,
            active,
        };

        try {
            if (isNew) {
                await apiService.post('/api/admin/prizes/categories', categoryData);
            } else {
                await apiService.put(`/api/admin/prizes/categories/${id}`, categoryData);
            }
            setSnackbar({open: true, message: 'Category saved successfully!'});
            // Navigate back to the list after a short delay
            setTimeout(() => navigate('/prize-categories'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save category');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;

    const requiredAction = isNew ? 'create' : 'update';

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Prize Category' : `Edit Category: ${name}`}
            </Typography>

            <Protected component={PrizeCategoryEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Paper sx={{p: 3}}>
                        <Grid container spacing={3}>
                            <Grid size={{xs: 12}}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Category Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <TextField
                                    required
                                    fullWidth
                                    type="number"
                                    label="Sort Order"
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(parseInt(e.target.value, 10))}
                                />
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <FormControlLabel
                                    control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)}/>}
                                    label="Active"
                                />
                            </Grid>
                        </Grid>

                        {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
                    </Paper>

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={PrizeCategoryEditPage} action={requiredAction}>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save Category'}
                            </Button>
                        </Protected>
                        <Button
                            component={RouterLink}
                            to="/prize-categories"
                            variant="outlined"
                            startIcon={<ArrowBackIcon/>}
                        >
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

PrizeCategoryEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default PrizeCategoryEditPage;